import { BaseSignatureInserter } from "./BaseSignatureInserter";
import { info, warn, error } from "@/shared/services/logger";
import type { InsertResult } from "@/shared/types/inserter";

const SIGNATURE_START_MARKER = "<!-- HELPITECH_SIG_START -->";
const SIGNATURE_END_MARKER = "<!-- HELPITECH_SIG_END -->";

/**
 * Implementa inserção de assinatura para o Outlook Mobile (Android/iOS).
 *
 * No mobile, apenas `setSignatureAsync` é suportado em Message Compose;
 * `prependAsync`/`setAsync` falham nesse modo (só Appointment Organizer).
 * A lógica compartilhada de orquestração fica em {@link BaseSignatureInserter}.
 *
 * @example
 * ```typescript
 * const inserter = new OutlookMobileSignatureInserter();
 * Office.actions.associate("checkSignature", (event) => inserter.insert(event));
 * ```
 */
export class OutlookMobileSignatureInserter extends BaseSignatureInserter {
  constructor() {
    super("OutlookMobileInserter");
  }

  /**
   * Insere HTML no corpo do item usando `setSignatureAsync` (mensagem) ou `setAsync` (compromisso).
   *
   * @param html - Conteúdo HTML da assinatura.
   * @param item - Item do Outlook.
   * @returns Promise com `InsertResult` detalhando sucesso/falha e método usado.
   *
   * @example
   * const result = await inserter.insertSignature(html, item);
   */
  insertSignature(html: string, item: Office.Item): Promise<InsertResult> {
    return new Promise((resolve) => {
      const body = (item as any).body;
      const isAppointment = (item as any).itemType === Office.MailboxEnums.ItemType.Appointment;

      if (isAppointment) {
        info(this.source, `[INSERT] Compromisso → setAsync (${html.length} chars).`);
        body.setAsync(html, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) {
            info(this.source, "[INSERT] setAsync OK.");
            resolve({ success: true, method: "setAsync" });
          } else {
            error(this.source, `[INSERT] setAsync falhou — code=${r.error?.code}, msg=${r.error?.message}`);
            resolve({ success: false, method: "setAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
          }
        });
        return;
      }

      // No Message Compose do mobile não há fallback viável: prependAsync/setAsync
      // não são suportados. Sem setSignatureAsync, reportamos falha em vez de
      // tentar um método que sabidamente retorna erro no Android/iOS.
      if (typeof body.setSignatureAsync !== "function") {
        warn(this.source, "[INSERT] setSignatureAsync indisponível no mobile — sem método de inserção.");
        resolve({ success: false, method: "setSignatureAsync", errorMessage: "setSignatureAsync indisponível no mobile" });
        return;
      }

      const markedHtml = "<br/>" + SIGNATURE_START_MARKER + html + SIGNATURE_END_MARKER;
      info(this.source, `[INSERT] setSignatureAsync (${html.length} chars).`);
      body.setSignatureAsync(markedHtml, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) {
          info(this.source, "[INSERT] setSignatureAsync OK.");
          resolve({ success: true, method: "setSignatureAsync" });
        } else {
          error(this.source, `[INSERT] setSignatureAsync falhou — code=${r.error?.code}, msg=${r.error?.message}`);
          resolve({ success: false, method: "setSignatureAsync", errorCode: r.error?.code, errorMessage: r.error?.message });
        }
      });
    });
  }
}
